import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '@/services/api';
import { Product, Category, Catalog, ApiResponse } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';

/**
 * Catalog State Interface
 */
interface CatalogState {
    products: Product[];
    categories: Category[];
    selectedCategory: number | null;
    searchQuery: string;
    searchResults: Product[];
    isLoading: boolean;
    error: string | null;
}

/**
 * Initial State
 */
const initialState: CatalogState = {
    products: [],
    categories: [],
    selectedCategory: null,
    searchQuery: '',
    searchResults: [],
    isLoading: false,
    error: null,
};

/**
 * Async Thunks
 */

// Fetch the full product list. Chips filter it locally (the server filter is an exact categoryId match
// and would bypass the offline cache). The backend never fills `categories` here; see fetchCategories.
export const fetchCatalog = createAsyncThunk(
    'catalog/fetchCatalog',
    async (params: { shopId: number }, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Catalog>>(API_ENDPOINTS.CATALOG(params.shopId));
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Categories come from their own endpoint (GET catalog/categories). Best-effort: a failure must not block the grid.
export const fetchCategories = createAsyncThunk(
    'catalog/fetchCategories',
    async (params: { shopId: number }, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Category[]>>(API_ENDPOINTS.CATEGORIES(params.shopId));
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Search products
export const searchProducts = createAsyncThunk(
    'catalog/searchProducts',
    async (params: { shopId: number; query: string; limit?: number }, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams({
                q: params.query,
                limit: (params.limit || 10).toString(),
            });

            const url = `${API_ENDPOINTS.SEARCH_PRODUCTS(params.shopId)}?${queryParams.toString()}`;

            const response = await api.get<ApiResponse<Product[]>>(url);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Lookup product by barcode
export const lookupBarcode = createAsyncThunk(
    'catalog/lookupBarcode',
    async (params: { shopId: number; barcode: string }, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Product>>(
                API_ENDPOINTS.BARCODE_LOOKUP(params.shopId, params.barcode)
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Get product details
export const fetchProductDetails = createAsyncThunk(
    'catalog/fetchProductDetails',
    async (params: { shopId: number; compositeId: number }, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Product>>(
                API_ENDPOINTS.PRODUCT_DETAILS(params.shopId, params.compositeId)
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

const cacheKey = (shopId: number) => `pos_catalog_${shopId}`;

/**
 * Catalog Slice
 */
const catalogSlice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        // Set selected category
        setSelectedCategory: (state, action: PayloadAction<number | null>) => {
            state.selectedCategory = action.payload;
        },

        // Set search query
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;

            // Clear search results if query is empty
            if (!action.payload.trim()) {
                state.searchResults = [];
            }
        },

        // Clear search
        clearSearch: (state) => {
            state.searchQuery = '';
            state.searchResults = [];
        },

        // Clear catalog
        clearCatalog: (state) => {
            state.products = [];
            state.categories = [];
            state.selectedCategory = null;
            state.searchQuery = '';
            state.searchResults = [];
            state.error = null;
        },

        // Clear error
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Catalog
        builder
            .addCase(fetchCatalog.pending, (state, action) => {
                state.error = null;
                // Paint the last full catalog for this shop while the network request runs
                if (state.products.length === 0) {
                    try {
                        const cached = localStorage.getItem(cacheKey(action.meta.arg.shopId));
                        if (cached) {
                            const c: Catalog = JSON.parse(cached);
                            state.products = c.products || [];
                            if (state.categories.length === 0) state.categories = c.categories || [];
                        }
                    } catch { /* ignore corrupt cache */ }
                }
                state.isLoading = state.products.length === 0;
            })
            .addCase(fetchCatalog.fulfilled, (state, action) => {
                state.isLoading = false;
                state.products = action.payload.products || [];
                try {
                    localStorage.setItem(cacheKey(action.meta.arg.shopId), JSON.stringify({ ...action.payload, categories: state.categories }));
                } catch { /* quota exceeded: cache is best-effort */ }
            })
            .addCase(fetchCatalog.rejected, (state, action) => {
                state.isLoading = false;
                // Offline with a cached catalog: keep showing it silently
                if (state.products.length === 0) {
                    state.error = action.payload as string;
                }
            });

        // Fetch Categories: keeps the previous/cached list on failure, drops a selection that no longer exists
        builder.addCase(fetchCategories.fulfilled, (state, action) => {
            state.categories = action.payload || [];
            if (state.selectedCategory !== null && !state.categories.some((c) => c.id === state.selectedCategory)) {
                state.selectedCategory = null;
            }
            try {
                const key = cacheKey(action.meta.arg.shopId);
                const cached = localStorage.getItem(key);
                if (cached) localStorage.setItem(key, JSON.stringify({ ...JSON.parse(cached), categories: state.categories }));
            } catch { /* best-effort */ }
        });

        // Search Products
        builder
            .addCase(searchProducts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(searchProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.searchResults = action.payload;
            })
            .addCase(searchProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.searchResults = [];
            });

        // Lookup Barcode
        builder
            .addCase(lookupBarcode.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(lookupBarcode.fulfilled, (state) => {
                state.isLoading = false;
                // Product will be added to cart directly, no state update needed here
            })
            .addCase(lookupBarcode.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Fetch Product Details
        builder
            .addCase(fetchProductDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProductDetails.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchProductDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setSelectedCategory,
    setSearchQuery,
    clearSearch,
    clearCatalog,
    clearError,
} = catalogSlice.actions;

export default catalogSlice.reducer;